import { devToolsSlowQuerySleepIfEnabled } from "@/util/devtools";
import type { FunctionComponent } from "react";
import { lazy } from "react";
import { Text } from "@/client/ui/Text";

export default function UiDemoIndexPage() {
  return <Text className="text-muted-fg">Choose a demo from the list</Text>;
}

const lazyDemo = <Demo extends FunctionComponent>(
  importFn: () => Promise<{ default: Demo }>,
) =>
  lazy(async () => {
    await devToolsSlowQuerySleepIfEnabled();
    return await importFn();
  });

// prettier-ignore
export const demos: Record<string, FunctionComponent> = {
  // <pyly-glob-template glob="../../../**/*.demo.tsx" template="  [`${filenameWithoutExt.split('.')[0]}`]: lazyDemo(() => import(`${pathWithoutExt}`)),">
  [`AiMeaningHintModal`]: lazyDemo(() => import(`../../../client/ui/AiMeaningHintModal.demo`)),
  [`AssetImage`]: lazyDemo(() => import(`../../../client/ui/AssetImage.demo`)),
  [`Breadcrumbs`]: lazyDemo(() => import(`../../../client/ui/Breadcrumbs.demo`)),
  [`ButtonGroup`]: lazyDemo(() => import(`../../../client/ui/ButtonGroup.demo`)),
  [`CharacterWriting`]: lazyDemo(() => import(`../../../client/ui/CharacterWriting.demo`)),
  [`CloseButton`]: lazyDemo(() => import(`../../../client/ui/CloseButton.demo`)),
  [`Colors`]: lazyDemo(() => import(`../../../client/ui/Colors.demo`)),
  [`ComponentLozenge`]: lazyDemo(() => import(`../../../client/ui/ComponentLozenge.demo`)),
  [`DropdownMenu2`]: lazyDemo(() => import(`../../../client/ui/DropdownMenu2.demo`)),
  [`FloatingMenuModal`]: lazyDemo(() => import(`../../../client/ui/FloatingMenuModal.demo`)),
  [`GlyphBuilder`]: lazyDemo(() => import(`../../../client/ui/GlyphBuilder.demo`)),
  [`HanziDecompositionEditor`]: lazyDemo(() => import(`../../../client/ui/HanziDecompositionEditor.demo`)),
  [`HanziGraphic`]: lazyDemo(() => import(`../../../client/ui/HanziGraphic.demo`)),
  [`HanziHintOption`]: lazyDemo(() => import(`../../../client/ui/HanziHintOption.demo`)),
  [`HanziText`]: lazyDemo(() => import(`../../../client/ui/HanziText.demo`)),
  [`HanziWritingDebugViewer`]: lazyDemo(() => import(`../../../client/ui/HanziWritingDebugViewer.demo`)),
  [`HeaderProvider`]: lazyDemo(() => import(`../../../client/ui/HeaderProvider.demo`)),
  [`HskLozenge`]: lazyDemo(() => import(`../../../client/ui/HskLozenge.demo`)),
  [`Icon`]: lazyDemo(() => import(`../../../client/ui/Icon.demo`)),
  [`ImageCloud`]: lazyDemo(() => import(`../../../client/ui/ImageCloud.demo`)),
  [`ImagePasteDropZone`]: lazyDemo(() => import(`../../../client/ui/ImagePasteDropZone.demo`)),
  [`InlineEditableSettingImage`]: lazyDemo(() => import(`../../../client/ui/InlineEditableSettingImage.demo`)),
  [`InlineEditableSettingText`]: lazyDemo(() => import(`../../../client/ui/InlineEditableSettingText.demo`)),
  [`Lozenge`]: lazyDemo(() => import(`../../../client/ui/Lozenge.demo`)),
  [`MenuDictionarySearch`]: lazyDemo(() => import(`../../../client/ui/MenuDictionarySearch.demo`)),
  [`NewSkillModal`]: lazyDemo(() => import(`../../../client/ui/NewSkillModal.demo`)),
  [`NewSkillModalContentNewPronunciation`]: lazyDemo(() => import(`../../../client/ui/NewSkillModalContentNewPronunciation.demo`)),
  [`NewSkillModalContentNewWord`]: lazyDemo(() => import(`../../../client/ui/NewSkillModalContentNewWord.demo`)),
  [`NewSprout`]: lazyDemo(() => import(`../../../client/ui/NewSprout.demo`)),
  [`NewWordTutorial`]: lazyDemo(() => import(`../../../client/ui/NewWordTutorial.demo`)),
  [`PinyinOptionButton`]: lazyDemo(() => import(`../../../client/ui/PinyinOptionButton.demo`)),
  [`PylyMdxComponents`]: lazyDemo(() => import(`../../../client/ui/PylyMdxComponents.demo`)),
  [`Pylymark`]: lazyDemo(() => import(`../../../client/ui/Pylymark.demo`)),
  [`PylymarkTypewriter`]: lazyDemo(() => import(`../../../client/ui/PylymarkTypewriter.demo`)),
  [`QuickSearchButton`]: lazyDemo(() => import(`../../../client/ui/QuickSearchButton.demo`)),
  [`QuickSearchModal`]: lazyDemo(() => import(`../../../client/ui/QuickSearchModal.demo`)),
  [`QuizDeckHanziCharacterWritingQuestion`]: lazyDemo(() => import(`../../../client/ui/QuizDeckHanziCharacterWritingQuestion.demo`)),
  [`QuizDeckHanziWordToGlossTypedQuestion`]: lazyDemo(() => import(`../../../client/ui/QuizDeckHanziWordToGlossTypedQuestion.demo`)),
  [`QuizDeckHanziWordToPinyinTypedQuestion`]: lazyDemo(() => import(`../../../client/ui/QuizDeckHanziWordToPinyinTypedQuestion.demo`)),
  [`QuizDeckOneCorrectPairQuestion`]: lazyDemo(() => import(`../../../client/ui/QuizDeckOneCorrectPairQuestion.demo`)),
  [`QuizDeckResultToast`]: lazyDemo(() => import(`../../../client/ui/QuizDeckResultToast.demo`)),
  [`QuizFlagText`]: lazyDemo(() => import(`../../../client/ui/QuizFlagText.demo`)),
  [`QuizProgressBar`]: lazyDemo(() => import(`../../../client/ui/QuizProgressBar.demo`)),
  [`QuizQueueButton`]: lazyDemo(() => import(`../../../client/ui/QuizQueueButton.demo`)),
  [`QuizQueueLozenge`]: lazyDemo(() => import(`../../../client/ui/QuizQueueLozenge.demo`)),
  [`RectButton`]: lazyDemo(() => import(`../../../client/ui/RectButton.demo`)),
  [`Select`]: lazyDemo(() => import(`../../../client/ui/Select.demo`)),
  [`ShimmerRect`]: lazyDemo(() => import(`../../../client/ui/ShimmerRect.demo`)),
  [`ShimmerText`]: lazyDemo(() => import(`../../../client/ui/ShimmerText.demo`)),
  [`ShootingStars`]: lazyDemo(() => import(`../../../client/ui/ShootingStars.demo`)),
  [`SkillProgressBar`]: lazyDemo(() => import(`../../../client/ui/SkillProgressBar.demo`)),
  [`StartupLoader`]: lazyDemo(() => import(`../../../client/ui/StartupLoader.demo`)),
  [`StrokeSpec`]: lazyDemo(() => import(`../../../client/ui/StrokeSpec.demo`)),
  [`Tabs`]: lazyDemo(() => import(`../../../client/ui/Tabs.demo`)),
  [`TextAnswerButton`]: lazyDemo(() => import(`../../../client/ui/TextAnswerButton.demo`)),
  [`TextAnswerInputSingle`]: lazyDemo(() => import(`../../../client/ui/TextAnswerInputSingle.demo`)),
  [`TextInputMulti`]: lazyDemo(() => import(`../../../client/ui/TextInputMulti.demo`)),
  [`TextInputSingle`]: lazyDemo(() => import(`../../../client/ui/TextInputSingle.demo`)),
  [`Theme`]: lazyDemo(() => import(`../../../client/ui/Theme.demo`)),
  [`ThreeSplitLinesDown`]: lazyDemo(() => import(`../../../client/ui/ThreeSplitLinesDown.demo`)),
  [`ToggleButton`]: lazyDemo(() => import(`../../../client/ui/ToggleButton.demo`)),
  [`Tooltip`]: lazyDemo(() => import(`../../../client/ui/Tooltip.demo`)),
  [`TutorialDialogBox`]: lazyDemo(() => import(`../../../client/ui/TutorialDialogBox.demo`)),
  [`Typography`]: lazyDemo(() => import(`../../../client/ui/Typography.demo`)),
  [`WikiHanziCharacterMeaning`]: lazyDemo(() => import(`../../../client/ui/WikiHanziCharacterMeaning.demo`)),
  [`WikiHanziModal`]: lazyDemo(() => import(`../../../client/ui/WikiHanziModal.demo`)),
  [`WikiHanziProgressLozenge`]: lazyDemo(() => import(`../../../client/ui/WikiHanziProgressLozenge.demo`)),
// </pyly-glob-template>
};
